import { useCallback } from 'react';
import { useMutation } from '@ts-core/lib/apollo';

import { getErrorMessage } from '@ts-core/lib/apollo';
import { useFileUploader } from '@ts-core/core/hooks/utils';
import { useSnackbarNotifications } from '@ts-core/core/hooks/ui';
import { removeItemFromCache } from '@ts-core/core/utils/apolloUtils';

import { ProfessorActivityAnswer } from '../types';

import { getActivityAnswersQuery, GetActivityAnswersQueryResult, GetActivityAnswersQueryVariables } from '../graphql/queries/getActivityAnswersQuery';

import {
  gradeActivityAnswerMutation,
  GradeActivityAnswerMutationResult,
  GradeActivityAnswerMutationVariables,
} from '../graphql/mutations/gradeActivityAnswerMutation';
import {
  createActivityAnswerFeedbackMutation,
  CreateActivityAnswerFeedbackMutationResult,
  CreateActivityAnswerFeedbackMutationVariables,
} from '../graphql/mutations/createActivityAnswerFeedbackMutation';
import {
  deleteActivityAnswerFeedbackMutation,
  DeleteActivityAnswerFeedbackMutationResult,
  DeleteActivityAnswerFeedbackMutationVariables,
} from '../graphql/mutations/deleteActivityAnswerFeedbackMutation';
import {
  createActivityAnswerScoreMutation,
  CreateActivityAnswerScoreMutationResult,
  CreateActivityAnswerScoreMutationVariables,
} from '../graphql/mutations/createActivityAnswerScoreMutation';
import {
  deleteActivityAnswerScoreMutation,
  DeleteActivityAnswerScoreMutationResult,
  DeleteActivityAnswerScoreMutationVariables,
} from '../graphql/mutations/deleteActivityAnswerScoreMutation';
import {
  deleteActivityAnswerMutation,
  DeleteActivityAnswerMutationResult,
  DeleteActivityAnswerMutationVariables,
} from '../graphql/mutations/deleteActivityAnswerMutation';

interface ActivityAnswerActionsHookInput {
  courseId: string;
  activityId: string;
}

interface ActivityAnswerActionsHookPayload {
  onDeleteAnswer: (answer: ProfessorActivityAnswer) => void;

  onGradeActivityAnswer: (answerId: string, studentId: string, file?: File, text?: string, score?: string) => void;
  gradeActivityAnswerLoading: boolean;
  gradeActivityAnswerError?: string;

  onCreateFeedback: (answerId: string, studentId: string, file?: File, text?: string) => void;
  createFeedbackLoading: boolean;
  createFeedbackError?: string;

  onDeleteFeedback: (answer: ProfessorActivityAnswer) => void;

  onCreateScore: (answerId: string, score: string) => void;
  createScoreLoading: boolean;
  createScoreError?: string;

  onDeleteScore: (answer: ProfessorActivityAnswer) => void;
}

export default function useActivityAnswerActions(input: ActivityAnswerActionsHookInput): ActivityAnswerActionsHookPayload {
  const { courseId, activityId } = input;
  const { onUploadFile, error: uploadFileError, uploading } = useFileUploader();
  const { showSuccessNotification, showErrorNotification } = useSnackbarNotifications();

  /**
   * Delete Answer
   */
  const [deleteAnswer] = useMutation<DeleteActivityAnswerMutationResult, DeleteActivityAnswerMutationVariables>(deleteActivityAnswerMutation, {
    onError: showErrorNotification,
    update: removeItemFromCache<DeleteActivityAnswerMutationResult, GetActivityAnswersQueryResult, GetActivityAnswersQueryVariables>({
      query: getActivityAnswersQuery,
      queryVariables: { courseId, activityId },
      queryName: 'answers',
      idAccessor: 'id',
      mutationName: 'answer',
    }),
  });
  const onDeleteAnswer = useCallback(
    (answer: ProfessorActivityAnswer) => {
      deleteAnswer({
        variables: { courseId, activityId, answerId: answer.id },
        optimisticResponse: {
          answer: {
            id: answer.id,
          },
        },
      });
    },
    [courseId, activityId, deleteAnswer]
  );

  /**
   * Full grade
   */
  const [gradeActivityAnswer, { loading: gradeActivityAnswerLoading, error: gradeActivityAnswerError }] = useMutation<
    GradeActivityAnswerMutationResult,
    GradeActivityAnswerMutationVariables
  >(gradeActivityAnswerMutation, { onCompleted: () => showSuccessNotification('Los datos fueron guardados correctamente.') });
  const onGradeActivityAnswer = useCallback(
    (answerId: string, studentId: string, file?: File, text?: string, score?: string) => {
      function gradeAnswer(score?: string, fileInput?: { path: string; name: string }, text?: string) {
        const hasFeedback = !!(fileInput || text);

        gradeActivityAnswer({
          variables: {
            courseId,
            activityId,
            answerId,
            input: {
              score,
              feedback: hasFeedback ? { file: fileInput, text } : undefined,
            },
          },
        });
      }

      if (file) {
        onUploadFile(
          file,
          'aws/signed-url/activities',
          { courseId, activityId, studentId, answerId, fileName: file.name },
          {
            onSuccess: (data) => {
              gradeAnswer(score, { path: data.path, name: file.name }, text);
            },
          }
        );
      } else {
        gradeAnswer(score, undefined, text);
      }
    },
    [courseId, activityId, gradeActivityAnswer, onUploadFile]
  );

  /**
   * Only feedback
   */
  const [createFeedback, { loading: createFeedbackLoading, error: createFeedbackError }] = useMutation<
    CreateActivityAnswerFeedbackMutationResult,
    CreateActivityAnswerFeedbackMutationVariables
  >(createActivityAnswerFeedbackMutation);
  const onCreateFeedback = useCallback(
    (answerId: string, studentId: string, file?: File, text?: string) => {
      function callMutation(fileInput?: { path: string; name: string }, text?: string) {
        createFeedback({
          variables: {
            courseId,
            activityId,
            answerId,
            input: {
              text,
              file: fileInput,
            },
          },
        });
      }

      if (file) {
        onUploadFile(
          file,
          'aws/signed-url/activities',
          { courseId, activityId, studentId, answerId, fileName: file.name },
          {
            onSuccess: (data) => {
              callMutation({ path: data.path, name: file.name }, text);
            },
          }
        );
      } else {
        callMutation(undefined, text);
      }
    },
    [createFeedback, courseId, activityId, onUploadFile]
  );

  const [deleteFeedback] = useMutation<DeleteActivityAnswerFeedbackMutationResult, DeleteActivityAnswerFeedbackMutationVariables>(
    deleteActivityAnswerFeedbackMutation,
    { onError: showErrorNotification }
  );
  const onDeleteFeedback = useCallback(
    (answer: ProfessorActivityAnswer) => {
      deleteFeedback({
        variables: {
          courseId,
          activityId,
          answerId: answer.id,
        },
        optimisticResponse: {
          answer: {
            ...answer,
            feedback: null,
          },
        },
      });
    },
    [deleteFeedback, courseId, activityId]
  );

  /**
   * Score only
   */
  const [createScore, { loading: createScoreLoading, error: createScoreError }] = useMutation<
    CreateActivityAnswerScoreMutationResult,
    CreateActivityAnswerScoreMutationVariables
  >(createActivityAnswerScoreMutation);
  const onCreateScore = useCallback(
    (answerId: string, score: string) => {
      createScore({
        variables: {
          courseId,
          activityId,
          answerId,
          input: {
            score,
          },
        },
      });
    },
    [createScore, courseId, activityId]
  );
  const [deleteScore] = useMutation<DeleteActivityAnswerScoreMutationResult, DeleteActivityAnswerScoreMutationVariables>(
    deleteActivityAnswerScoreMutation,
    { onError: showErrorNotification }
  );
  const onDeleteScore = useCallback(
    (answer: ProfessorActivityAnswer) => {
      deleteScore({
        variables: {
          courseId,
          activityId,
          answerId: answer.id,
        },
        optimisticResponse: {
          answer: {
            ...answer,
            score: null,
          },
        },
      });
    },
    [courseId, activityId, deleteScore]
  );

  return {
    onDeleteAnswer,

    onGradeActivityAnswer,
    gradeActivityAnswerLoading: uploading || gradeActivityAnswerLoading,
    gradeActivityAnswerError: uploadFileError || getErrorMessage({ error: gradeActivityAnswerError }),

    onCreateFeedback,
    createFeedbackLoading,
    createFeedbackError: uploadFileError || getErrorMessage({ error: createFeedbackError }),

    onDeleteFeedback,

    onCreateScore,
    createScoreLoading,
    createScoreError: getErrorMessage({ error: createScoreError }),

    onDeleteScore,
  };
}
